import React, { useEffect, useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import Button from './Button';
import { authColors, shadows } from '../../styles/colors';
import Spinner from './Spinner';

interface LoadingWithHelpProps {
  loading: boolean;
  timeoutMs?: number; // Default: 8000ms (8 seconds)
  fullScreen?: boolean; // Default: false
  inline?: boolean; // Default: false - if true, only shows help message (no spinner)
  className?: string;
}

/**
 * Standard loading component with help buttons after timeout.
 * 
 * UX Standard: Show help after 8 seconds (balance between not being intrusive and reassuring)
 * - 0-1s: No indicator needed
 * - 1-3s: Simple spinner
 * - 3-10s: Spinner + message
 * - 10s+: Help message with actions (we show at 8s as modern apps do)
 */
const LoadingWithHelp: React.FC<LoadingWithHelpProps> = ({
  loading,
  timeoutMs = 8000,
  fullScreen = false,
  inline = false,
  className = '',
}) => {
  const { t } = useI18n();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!loading) {
      setShowHelp(false);
      return;
    }
    // Show help after timeout (standard: 8 seconds)
    const helpTimer = setTimeout(() => setShowHelp(true), timeoutMs);
    return () => {
      clearTimeout(helpTimer);
    };
  }, [loading, timeoutMs]);

  // In inline mode, only show help message (no spinner)
  if (inline) {
    if (!loading || !showHelp) {
      return null;
    }
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-xl p-4 ${className}`}>
        <p className="text-gray-900 font-semibold text-sm text-center">
          {t('common.loadingTakingLongTitle')}
        </p>
        <p className="text-gray-600 text-sm text-center mt-1">
          {t('common.loadingTakingLongMessage')}
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            {t('common.reload')}
          </Button>
          <Button
            type="button"
            variant="primary"
            className="w-full"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            {t('common.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Full screen or regular mode with spinner
  if (!loading) {
    return null;
  }

  const containerClasses = fullScreen
    ? `min-h-screen flex flex-col items-center justify-center px-4 ${authColors.backgroundGradient}`
    : `flex items-center justify-center ${className}`;

  const cardClasses = fullScreen
    ? `bg-white rounded-2xl p-8 w-full max-w-md ${shadows.large} border border-gray-100`
    : 'text-center';

  return (
    <div className={containerClasses}>
      <div className={cardClasses}>
        <div className="flex flex-col items-center text-center">
          <Spinner size="lg" label={t('common.loading')} />
        </div>
        {showHelp && (
          <div className="mt-6">
            <p className="text-gray-900 font-semibold">{t('common.loadingTakingLongTitle')}</p>
            <p className="text-gray-600 text-sm mt-2">{t('common.loadingTakingLongMessage')}</p>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => window.location.reload()}
              >
                {t('common.reload')}
              </Button>
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                {t('common.backToHome')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingWithHelp;

